/*
  Split test `pdp-tool-promo`: the featured tool's second checkbox and its
  how-to popup.

  A standalone file on purpose. The add-on panel already owns the only thing
  that carries money here, properties[_addon_variant_ids], which it builds from
  `[data-wb-tile-input]:checked` inside `[data-pdp-addons]`. The tool keeps its
  tile in the Tools tab, so nothing about the cart changes.

  What is here is everything that sits outside the panel:

  1. The two checkboxes, above Add to cart and in the strap-change accordion.
     Neither has a `name` and neither reaches the form. They tick the panel's
     tile and follow it back, so the tool appears in three places and can still
     only be selected once.
  2. The popup, opened from either one, with the video inserted on open so a page
     nobody opens it on makes no request to YouTube.
*/
(function () {
  'use strict';

  var PANEL = '[data-pdp-addons]';

  function tilesFor(variantId) {
    if (!variantId) return [];
    return Array.prototype.slice.call(
      document.querySelectorAll(
        PANEL + ' [data-wb-tile][data-wb-variant-id="' + variantId + '"] [data-wb-tile-input]'
      )
    );
  }

  function proxies() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-wb-tool-proxy]'));
  }

  /* ------------------------------------------------------------- selection */

  // The panel's tile is the truth, so this only ever reads it.
  function readPanel(variantId) {
    return tilesFor(variantId).some(function (input) {
      return input.checked;
    });
  }

  /*
    Writing goes through a change event rather than calling the panel directly.
    The panel listens for `change` on its own root and re-syncs from there, so
    the hidden cart inputs and the Add to cart price follow without this file
    knowing anything about either.
  */
  function writePanel(variantId, on) {
    var inputs = tilesFor(variantId);
    if (!inputs.length) return false;

    var target = inputs[0];

    if (on) {
      // A radio in a group: checking this one clears any other tool.
      target.checked = true;
    } else {
      inputs.forEach(function (input) {
        input.checked = false;
      });
    }

    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function syncProxies() {
    proxies().forEach(function (proxy) {
      var on = readPanel(proxy.getAttribute('data-wb-variant-id'));
      if (proxy.checked !== on) proxy.checked = on;
    });
    syncVisibility();
  }

  /*
    The tools in the panel are one radio group, so ticking this checkbox would
    silently swap out a tool the shopper already chose in the Tools tab, and
    the checkbox reads as "add", not "replace". When another tool is selected
    the whole block steps aside; it comes back the moment that pick is cleared
    or the featured tool takes its place.
  */
  function displacedBy(variantId) {
    var inputs = tilesFor(variantId);
    if (!inputs.length) return false;
    var group = inputs[0].name;
    if (!group) return false;
    var others = document.querySelectorAll(PANEL + ' [data-wb-tile-input][name="' + group + '"]:checked');
    return Array.prototype.some.call(others, function (input) {
      return String(input.value) !== String(variantId);
    });
  }

  function syncVisibility() {
    document.querySelectorAll('[data-wb-tool-promo]').forEach(function (promo) {
      var proxy = promo.querySelector('[data-wb-tool-proxy]');
      var id = proxy && proxy.getAttribute('data-wb-variant-id');
      var alive = !!id && tilesFor(id).length > 0;
      promo.hidden = !alive || displacedBy(id);
    });
  }

  /* ----------------------------------------------------------------- popup */

  function modal() {
    return document.querySelector('[data-wb-tool-modal]');
  }

  /*
    The popup is rendered inside the product section, and .product__module is
    position: relative with z-index 19, which makes it a stacking context. Inside
    it the popup's z-index 40 counts for nothing against the sticky header (21)
    and announcement bar (25), which live at the root: measured on a 390px
    viewport, elementFromPoint over the header returned .header--Wrapper while
    the scrim was underneath it. So the popup opened under the site header.

    Moving it to <body> on open takes it out of that context for good.

    Done here rather than by unsetting that z-index in CSS: 19 is load bearing
    for the section, and the popup should not depend on it staying a particular
    value.
  */
  function portal(root) {
    if (!root || root.parentNode === document.body) return;
    /* A section re-render can build a second popup; do not leave both. */
    var stale = document.body.querySelector(':scope > [data-wb-tool-modal]');
    if (stale && stale !== root) stale.remove();
    document.body.appendChild(root);
  }

  /*
    Accepts a full watch URL, a youtu.be link, an embed URL or a bare id, so a
    merchant can paste whatever YouTube handed them into the setting.
  */
  function videoId(value) {
    if (!value) return '';
    var text = String(value).trim();
    var match = text.match(/(?:[?&]v=|youtu\.be\/|\/embed\/)([A-Za-z0-9_-]{6,})/);
    if (match) return match[1];
    return /^[A-Za-z0-9_-]{6,}$/.test(text) ? text : '';
  }

  /*
    Which video a trigger opens on, most specific first: its own, then any other
    trigger on this page, then the setting. The middle step is what gets the row
    above Add to cart the video for this strap family: only the accordion knows
    it, because only the accordion's own answer links to it.
  */
  function videoFor(trigger) {
    var own = videoId(trigger && trigger.getAttribute('data-wb-tool-video'));
    if (own) return own;

    var found = '';
    document.querySelectorAll('[data-wb-tool-info][data-wb-tool-video]').forEach(function (other) {
      if (found) return;
      found = videoId(other.getAttribute('data-wb-tool-video'));
    });
    if (found) return found;

    var root = modal();
    return root ? videoId(root.getAttribute('data-wb-tool-default-video')) : '';
  }

  var lastTrigger = null;
  var previousOverflow = '';
  var lockedTarget = null;

  /*
    The theme's own lock, which every other overlay here uses.

    `document.body.style.overflow` is not a scroll lock on iOS Safari: the page
    keeps scrolling under the scrim. bodyScrollLock and IOSScrollLock come from
    libs.js and are what global.js reaches for, and `[data-scrollable]` is
    already on the popup's body for them to allow. The overflow line stays as
    the fallback for a page where libs.js never loaded.
  */
  function lockScroll(root) {
    lockedTarget = root.querySelector('[data-scrollable]');
    if (window.bodyScrollLock && lockedTarget) {
      window.bodyScrollLock.disableBodyScroll(lockedTarget, window.bodyScrollLockOptions);
      if (window.IOSScrollLock && window.IOSScrollLock.lock) window.IOSScrollLock.lock();
      return;
    }
    lockedTarget = null;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (window.bodyScrollLock && lockedTarget) {
      window.bodyScrollLock.enableBodyScroll(lockedTarget);
      if (window.IOSScrollLock && window.IOSScrollLock.unlock) window.IOSScrollLock.unlock();
      lockedTarget = null;
      return;
    }
    document.body.style.overflow = previousOverflow;
  }

  function openModal(trigger) {
    var root = modal();
    if (!root) return;

    /* Before the iframe is built, so nothing is reloaded by the move. */
    portal(root);

    lastTrigger = trigger || null;

    var frame = root.querySelector('[data-wb-tool-modal-video]');
    var dialog = root.querySelector('[data-wb-tool-dialog]');
    var id = videoFor(trigger);
    var titleEl = root.querySelector('#wb-tool-modal-title');
    var frameTitle = ((titleEl && titleEl.textContent) || 'Video').trim().replace(/"/g, '&quot;');
    if (frame) {
      // Built here rather than rendered, so no page view costs a request to
      // YouTube for a popup that is usually never opened.
      frame.innerHTML = id
        ? '<iframe src="https://www.youtube-nocookie.com/embed/' +
          id +
          '?rel=0&modestbranding=1" title="' + frameTitle + '" allow="accelerometer; encrypted-media; picture-in-picture" allowfullscreen loading="lazy"></iframe>'
        : '';
    }
    // Drops the video column rather than leaving an empty black half.
    if (dialog) dialog.setAttribute('data-wb-tool-layout', id ? 'video' : 'bare');

    root.hidden = false;
    lockScroll(root);

    // The scrim carries the same data attribute and comes first in the DOM;
    // a div cannot take focus, so focus the button itself.
    var close = root.querySelector('button[data-wb-tool-modal-close]') || root.querySelector('[data-wb-tool-modal-close]');
    if (close) close.focus();
  }

  function closeModal() {
    var root = modal();
    if (!root || root.hidden) return;

    // Emptying the frame is what stops playback. `hidden` alone leaves the
    // video running behind a closed popup, audio and all.
    var frame = root.querySelector('[data-wb-tool-modal-video]');
    if (frame) frame.innerHTML = '';

    root.hidden = true;
    unlockScroll();

    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(event) {
    var root = modal();
    if (!root || root.hidden || event.key !== 'Tab') return;

    var items = Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (item) {
      return item.offsetParent !== null;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ----------------------------------------------------------------- wiring */

  function bind() {
    document.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var trigger = event.target.closest('[data-wb-tool-info]');
      if (trigger) {
        event.preventDefault();
        openModal(trigger);
        return;
      }

      if (event.target.closest('[data-wb-tool-modal-close]')) {
        closeModal();
        return;
      }

      var add = event.target.closest('[data-wb-tool-modal-add]');
      if (!add) return;

      // The popup's own add goes through the same one place as both checkboxes.
      // The id comes off the popup, which was rendered for this tool: reading it
      // off whichever tile happens to be first in the panel picks the buckle.
      var root = modal();
      writePanel(root && root.getAttribute('data-wb-tool-variant-id'), true);
      syncProxies();
      closeModal();
    });

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || typeof target.matches !== 'function') return;

      if (target.matches('[data-wb-tool-proxy]')) {
        var applied = writePanel(target.getAttribute('data-wb-variant-id'), target.checked);
        // No tile to write to means the panel is not on this page. Put the
        // checkbox back rather than leaving it claiming something is in the
        // order when nothing is.
        if (!applied) target.checked = false;
      }
    });

    /*
      The panel announces its own state after every sync, which is the only
      signal that catches all of them: a tile's own `change`, a click on the row
      body, a mousedown or Space that clears a choice, and the re-adoption of the
      markup after a colour change re-renders the buy column.
    */
    document.addEventListener('pdp-addons:changed', syncProxies);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      trapFocus(event);
    });
  }

  /*
    A checkbox with no tile behind it is not a checkbox, it is a trap.

    The tile lives in the add-on panel and the accordion's copy of this card
    does not know whether the template rendered one: `product.mission-to-sun`
    carries the strap-change accordion and no panel at all, and a merchant can
    remove the panel from any template in the editor. There, ticking the box
    writes nothing, the change handler puts the tick straight back, and the
    popup's own add button is equally inert. So the whole block goes rather than
    sitting there refusing to work.

    The same for a trigger with no popup to open.
  */
  function hideDeadSurfaces() {
    syncVisibility();

    if (modal()) return;
    document.querySelectorAll('[data-wb-tool-info]').forEach(function (trigger) {
      trigger.hidden = true;
    });
  }

  function start() {
    hideDeadSurfaces();
    syncProxies();
  }

  bind();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
